import React, {useState} from 'react';
import 'react-spring-bottom-sheet/dist/style.css'
import styled from 'styled-components';
import {useToast} from "@chakra-ui/react";
import {Check, Delete, Flag, FlagOff, Gavel, Lock, MessageCircle, Pin, Tag} from "lucide-react";
import useModal from "../../modals";
import {useActions} from "../../app_context";
import {useRelativeTime, useUsername} from "../../store";
import useFeedback from "../../utils/use_feedback";
import {useApprove, useIgnoreReports, useLock, useModDistinguish, useRemove, useStickied} from "../../api/moderation";
import CreateModMessage from "../../modals/create_modmessage";
import ViewReportsSheet from "./mod/view_reports";
import BanSheet from "./mod/ban";
import ViewNotesSheet from "./mod/view_notes";
import Sheet, {MenuItem, MenuItemIcon, MenuItemText} from './sheet';

const Status = styled.div``;

const ModerateContentSheet = styled(({className, comment, post, onClose}) => {

    const modal = useModal();
    const actions = useActions();
    const username = useUsername();

    const type = post ? 'Post' : 'Comment';
    const content = post ? post : comment;
    const { id, community, moderation } = content;

    const isSelf = content.author === username;

    const isMarkedMod = content.is_moderator;
    const isRemoved = moderation.removed_at !== 0;
    const isApproved = moderation.approved_at !== 0;
    const ignoredReports = moderation.is_ignored_reports;
    const isReported = moderation.is_reported;
    const isLocked = Boolean(post?.is_locked);
    const isStickied = content.is_stickied;
    // const isModDistinguished = content.is_moderator;
    const removedBy = moderation.removed_by;
    const approvedBy = moderation.approved_by;
    // const isNSFW = Boolean(post?.is_nsfw);

    const actionAgo = useRelativeTime(isApproved ? moderation.approved_at : moderation.removed_at, false);

    const toast = useToast();

    const {feedbackCh} = useFeedback();

    const ignoreReports = useIgnoreReports(feedbackCh);
    const handleIgnoreReports = async () => {
        onClose();
        const action = type === 'Post' ? actions.postIgnoreReports(id, !ignoredReports) : actions.commentIgnoreReports(id, !ignoredReports);
        try {
            ignoreReports(id, type.toLowerCase(), !ignoredReports);
            toast({
                title: `${ignoredReports ? 'Unignored' : 'Ignored'} reports.`,
                status: 'success',
                isClosable: true,
            });
        } catch {
            action.undo?.();
        }
    };

    const approve = useApprove(feedbackCh);
    const handleApprove = async () => {
        onClose();
        const action = type === 'Post' ? actions.postApprove(id, username) : actions.commentApprove(id, username);
        try {
            approve(id, type.toLowerCase());
            if (!toast.isActive('approve-content')) {
                toast({
                    id: 'approve-content',
                    title: `Approved ${type}.`,
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch {
            action.undo?.();
        }
    };

    const remove = useRemove(feedbackCh);
    const handleRemove = async () => {
        onClose();
        const action = type === 'Post' ? actions.postRemove(id, username) : actions.commentRemove(id, username);
        try {
            remove(id, type.toLowerCase());
            if (!toast.isActive('remove-content')) {
                toast({
                    id: 'remove-content',
                    title: `Removed ${type}.`,
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch {
            action.undo?.();
        }
    };

    const lock = useLock(feedbackCh);
    const handleLock = async () => {
        onClose();
        const action = actions.postLock(id, !isLocked);
        try {
            lock(id, !isLocked);
            toast({
                title: `${isLocked ? 'Unlocked' : 'Locked'} post.`,
                status: 'success',
                isClosable: true,
            });
        } catch {
            action.undo?.();
        }
    };

    const sticky = useStickied(feedbackCh);
    const handleSticky = async () => {
        onClose();
        const action = type === 'Post' ? actions.postSticky(id, !isStickied) : actions.commentSticky(id, !isStickied);
        try {
            sticky(id, type.toLowerCase(), !isStickied);
            toast({
                title: `${isStickied ? 'Unstickied' : 'Stickied'} ${type}.`,
                status: 'success',
                isClosable: true,
            });
        } catch {
            action.undo?.();
        }
    };
    
    const handleMessage = () => {

        onClose();
        modal.open('CreateModMessage', {community, post, comment});

    };

    const [banOpen, setBanOpen] = useState(false);
    const handleBan = () => {
        setBanOpen(true);
    };

    const [reportsOpen, setReportsOpen] = useState(false);
    const handleReports = () => {
        setReportsOpen(true);
    };

    const [notesOpen, setNotesOpen] = useState(false);
    const handleNotes = () => {
        setNotesOpen(true);
    };

    const modDistinguish = useModDistinguish(feedbackCh);
    const handleMarkMod = async () => {
        onClose();
        const action = type === 'Post' ? actions.postModDistinguished(id, !isMarkedMod) : actions.commentModDistinguished(id, !isMarkedMod);
        try {
            modDistinguish(id, type, !isMarkedMod);
            toast({
                title: isMarkedMod ? 'Unmarked as mod.' : 'Marked as mod.',
                status: 'success',
                isClosable: true,
            });
        } catch {
            action.undo?.();
        }
    };

    return (
        <div className={className}>
            {(isApproved || isRemoved) &&
                <MenuItem>
                    <MenuItemText style={{fontWeight: 600, fontSize: 14}}>
                        {isRemoved ? 'Removed' : 'Approved'} {actionAgo} ago by {isRemoved ? removedBy : approvedBy}
                    </MenuItemText>
                </MenuItem>
            }
            {isReported &&
                <MenuItem onClick={handleReports}>
                    <MenuItemIcon>
                        <Flag size={24} />
                    </MenuItemIcon>
                    <MenuItemText>
                        View Reports
                    </MenuItemText>
                </MenuItem>
            }
            {(isReported || isRemoved) &&
                <MenuItem onClick={handleApprove}>
                    <MenuItemIcon>
                        <Check size={24} />
                    </MenuItemIcon>
                    <MenuItemText>
                        Approve
                    </MenuItemText>
                </MenuItem>
            }
            <MenuItem onClick={handleRemove}>
                <MenuItemIcon>
                    <Delete size={24} />
                </MenuItemIcon>
                <MenuItemText>
                    Remove
                </MenuItemText>
            </MenuItem>

            {(post || comment?.comment_parent_id === 0) &&
                <MenuItem onClick={handleSticky}>
                    <MenuItemIcon>
                        <Pin size={24} />
                    </MenuItemIcon>
                    <MenuItemText>
                        {isStickied ? 'Unsticky' : 'Sticky'}
                    </MenuItemText>
                </MenuItem>
            }

            <MenuItem>
                <MenuItemIcon onClick={handleIgnoreReports}>
                    <FlagOff size={24} />
                </MenuItemIcon>
                <MenuItemText>
                    {ignoredReports ? 'Unignore' : 'Ignore'}
                </MenuItemText>
            </MenuItem>

            {post &&
                <MenuItem onClick={handleLock}>
                    <MenuItemIcon>
                        <Lock size={24} />
                    </MenuItemIcon>
                    <MenuItemText>
                        {isLocked ? 'Unlock' : 'Lock'}
                    </MenuItemText>
                </MenuItem>
            }

            {comment && isSelf &&
                <MenuItem onClick={handleMarkMod}>
                    <MenuItemIcon>
                        <Lock size={24} />
                    </MenuItemIcon>
                    <MenuItemText>
                        {isMarkedMod ? 'Unmark as Mod' : 'Mark as Mod'}
                    </MenuItemText>
                </MenuItem>
            }

            <MenuItem onClick={handleNotes}>
                <MenuItemIcon>
                    <Tag size={24} />
                </MenuItemIcon>
                <MenuItemText>
                    Notes
                </MenuItemText>
            </MenuItem>
            <MenuItem onClick={handleBan}>
                <MenuItemIcon>
                    <Gavel size={24} />
                </MenuItemIcon>
                <MenuItemText>
                    Ban
                </MenuItemText>
            </MenuItem>
            <MenuItem onClick={handleMessage}>
                <MenuItemIcon>
                    <MessageCircle size={24} />
                </MenuItemIcon>
                <MenuItemText>
                    Message
                </MenuItemText>
            </MenuItem>
            {reportsOpen && <Sheet onClose={() => setReportsOpen(false) } padding={"header"}><ViewReportsSheet community={community} post={post} comment={comment} onClose={() => setReportsOpen(false) } /></Sheet>}
            {banOpen && <Sheet onClose={() => setBanOpen(false) } padding={"header"}><BanSheet community={community} username={content.author} onClose={() => setBanOpen(false) } /></Sheet>}
            {notesOpen && <Sheet onClose={() => setNotesOpen(false) } padding={"header"}><ViewNotesSheet community={community} username={content.author} onClose={() => setNotesOpen(false) } /></Sheet>}
        </div>
    );
})`
${Status} {
  font-weight: 500;
  font-size: 18px;
}
`;

export default ModerateContentSheet;
